import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { errMessage } from "@/lib/api";
import { usePageMeta } from "@/lib/format";
import { useT } from "@/i18n";
import { AuthShell } from "@/components/auth/AuthShell";
import { SocialButtons } from "@/components/auth/SocialButtons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";

export default function Login() {
  const t = useT();
  usePageMeta(t("login.meta_title"), t("login.meta_desc"));
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/app";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("login.welcome_back"));
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(errMessage(err, t("login.failed")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t("login.title")}
      subtitle={t("login.subtitle")}
      footer={<>{t("login.footer_q")} <Link to="/signup" className="font-medium text-primary hover:underline">{t("login.footer_link")}</Link></>}
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" autoComplete="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.email_placeholder")} data-testid="login-email" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">{t("auth.password")}</Label>
            <Link to="/forgot" className="text-xs text-muted-foreground hover:text-foreground">{t("login.forgot")}</Link>
          </div>
          <Input id="password" type="password" autoComplete="current-password" required value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password" />
        </div>
        <Button type="submit" className="w-full rounded-full" size="lg" disabled={loading} data-testid="login-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login.submit")}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> {t("common.or")} <div className="h-px flex-1 bg-border" />
      </div>
      <SocialButtons />
    </AuthShell>
  );
}
